import { useEffect, useState, useRef } from 'react';

import {
  fetchAuthenticatedUser,
  fetchCompetencies,
  fetchQuestions,
  fetchProsperContent,
  fetchTours,
  populateDataDictionary,
  fetchAllCareerPathContent,
  fetchUserInfo,
} from './useBootstrap.util';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useFetchUserProfileInfoQuery } from '../redux/profile/profile';
import { USER_PROFILE_FIELDS } from '../redux/profile/profile.constants';
import { setMostRecentResponseLoading } from '../redux/userResponse/userResponse';
import { getBackrsToken } from '../utils/tokenHelpers';

export type UseBootstrapResult = Readonly<{
  isBootstrapped: boolean;
}>;

export const useBootstrap = (): UseBootstrapResult => {
  const dispatch = useAppDispatch();
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  // Track with a ref to avoid duplicate calls
  const isBootstrappingRef = useRef(false);

  const user = useAppSelector((state) => state.user.user);

  useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !user?.id,
  });

  useEffect(() => {
    async function bootstrap() {
      if (isBootstrappingRef.current) return;

      isBootstrappingRef.current = true;

      try {
        //-----------------------------------------------
        // PHASE 1: We will wait to fetch the user, if there is an access token
        //-----------------------------------------------
        // Only wait for user authentication before marking app as bootstrapped
        const user = getBackrsToken() ? await fetchAuthenticatedUser(dispatch) : null;

        // App is now ready for basic interaction
        setIsBootstrapped(true);

        //-----------------------------------------------
        // PHASE 2: Background Data Loading
        //-----------------------------------------------
        const competenciesPromise = fetchCompetencies(dispatch);
        const questionsPromise = fetchQuestions(dispatch);
        const careerPathContentPromise = fetchAllCareerPathContent(dispatch);

        fetchTours(dispatch);

        competenciesPromise.then((competencies) => {
          if (competencies) {
            populateDataDictionary(dispatch, competencies);
          }
        });

        //-----------------------------------------------
        // PHASE 3: User-Specific Content (runs in background)
        //-----------------------------------------------
        if (user) {
          fetchProsperContent(dispatch);

          Promise.all([questionsPromise, careerPathContentPromise])
            .then(([questions, careerPathContent]) => {
              fetchUserInfo({ dispatch, questions, careerPathContent });
            })
            .catch((error) => {
              console.error('Failed to load user info dependencies', error);
            });
        } else {
          // if we don't have a user, we don't need to load the most recent response loading but still need to set the state (to ensure the unauthenticated user experience works)
          dispatch(setMostRecentResponseLoading(false));
        }
      } catch (error) {
        console.error('Failed to bootstrap app', error);
        setIsBootstrapped(false);
        isBootstrappingRef.current = false;
      } finally {
        isBootstrappingRef.current = false;
      }
    }

    // Start the bootstrap process
    bootstrap();
  }, [dispatch, user?.id]);

  return {
    isBootstrapped,
  };
};
